import React, { useImperativeHandle, forwardRef, useEffect, useState } from 'react';
import { useErrorInputs } from '../../../hooks/useErrorInputs';
import { useDataAuth } from '../../../hooks/useDataAuth';
import { useTranslation } from 'react-i18next';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Grid from '@mui/material/Grid';
import moment from 'moment';
import { GenericAutocomplete } from '../../../components/others/GenericAutocomplete';
import { getContactsConcat } from "../../../services/meetings.service";
import { fetchServiceJson, getClientAsociated } from '../../../services/common.services';
import { MultipleSelect } from '../../../components/others/MultipleSelect';
import '../../../styles/components/_forms.scss';

export const FormOpportunity = forwardRef(({ dto, handleInputChange, handleDirectInputChange, isView }, ref) => {
    const fieldsRequired = [
        'name', 
        'business_line', 
        'opportunity_stage', 
        'paymentTermDays', 
        'sale_type', 
        'contract_time',
        "id_contact",
        "id_client",
        'area',
        ...(((Array.isArray(dto.reasons_id) ? dto.reasons_id : String(dto.reasons_id || '').split(',')).map(String).map(s => s.trim()).some(r => ['7','14'].includes(r))) ? ['reason_other'] : [] )
    ];
    const daysPaymets = ['0', '15', '30', '45', '60'];
    const saleTypes = ['Venta Nueva', 'Upselling', 'Cross-selling', 'Renovación'];
    const [errorInputs, , , validForm] = useErrorInputs({});

    const [{ userSession }] = useDataAuth();
    const { t } = useTranslation();
    
    const [bussinesLine, setBussinesLine] = useState([]);
    const [oportunityStage, setOportunityStage] = useState([]);
    const [reasons, setReasons] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [bussines, setBussines] = useState([]);
    const [areas, setAreas] = useState([]);
    
    useImperativeHandle(ref, () => ({
        getData: () => dto,
        errorInputs,
        validForm: () => validForm(fieldsRequired, dto)
    }));

    useEffect(() => {
        const fetchData = async () => {
        try {
            const response = await fetchServiceJson("MENU-OPPORTUNITY", userSession.token, dto.id_company);
            if (response.data && response.data.length > 0) {
                const labels = response.data;
                labels.map((item) => {
                    const parsedValue = JSON.parse(item.value);
                    if (item.field === "areas") {
                        setAreas(parsedValue);
                    }
                    if (item.field === "businessLine") {
                        setBussinesLine(parsedValue);
                    }
                    if (item.field === "opportunityStage") {
                        setOportunityStage(parsedValue);
                    }
                    if (item.field === "reasons") { 
                        setReasons(parsedValue);
                    }
                });
            }
            const responseContacts = await getContactsConcat(userSession.token);
            if (responseContacts.data && responseContacts.data.length > 0) {
                setContacts(responseContacts.data);
            }
        } catch (e) {
            console.error("Error fetching areas JSON:", e.message);
        } finally {
            console.log("Finaliza la carga de datos");
        }
        };    fetchData();
    }, [userSession.token, dto.id_company]);

    useEffect(() => {
        const fetchClientAssociated = async () => {
          if (!dto.id_contact) return;
          try {
            setBussines([])
            const clientsAsociated = await getClientAsociated(
              { id_contact: `${dto.id_contact}` },
              userSession.token
            );
            if (clientsAsociated?.data?.length > 0) {
              setBussines(clientsAsociated.data);
              handleDirectInputChange("id_client", clientsAsociated.data[0].code);
            }
          } catch (error) {
            console.error("Error fetching associated clients:", error);
          }
        };
      
        fetchClientAssociated();
      }, [dto.id_contact]);
      
    

    const handleChangeDate = (e, name) => handleDirectInputChange(name, moment(e).format('YYYY/MM/DD'));

    const handleData = (e) => {
        const { name, value } = e.target;
        if ((['closing_probability', 'contract_time'].includes(name)) && value.length > 2) {
            return; 
        }
        if (name === 'extension' && value.length > 5) {
            return; 
        }
        if (name === 'reason_other' && value.length > 100) {
            return; 
        }
        if (name === 'opportunity_stage'){
            if(value !== 'Cierre No Ganado' && value !== 'Cierre Ganado'){
                handleDirectInputChange('reasons_id', []);
                handleDirectInputChange('reason_other', '');
            }
            if(value === 'Cierre No Ganado' && dto.reasons_id.length > 0){
                handleDirectInputChange('reasons_id', []);
                handleDirectInputChange('reason_other', '');
            }
            if(value === 'Cierre Ganado' && dto.reasons_id.length > 0){
                handleDirectInputChange('reasons_id', []);
                handleDirectInputChange('reason_other', '');
            }
        }
        handleDirectInputChange(name, value.toString());
    }

    const handleMultipleSelect = (event) => {
        const { target: { value } } = event;
        handleDirectInputChange('reasons_id', value.join(','));
    };

  return (
    <>
        <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid size={{ xs: 12, md: 4 }}>
                <TextField fullWidth required name="name" label={t('commonFields.name')} size="small"
                    value={dto.name}
                    error={Boolean(errorInputs.name?.isError)}
                    helperText={errorInputs.name?.messageError || ''}
                    onChange={handleInputChange}
                />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth required size="small"
                    error={Boolean(errorInputs.sale_type?.isError)}
                >
                    <InputLabel>{t('commonFields.saleType')}</InputLabel>
                    <Select name='sale_type' label={t('commonFields.saleType')}
                        value={dto.sale_type}
                        onChange={handleInputChange}
                        MenuProps={{PaperProps: {className: 'select-dropdown-scroll'}}}
                    >
                        {saleTypes.map(i => <MenuItem key={i} value={i}>{i}</MenuItem>)}
                    </Select>
                    {errorInputs.sale_type?.isError && ( <FormHelperText>{errorInputs.sale_type.messageError}</FormHelperText> )}
                </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <GenericAutocomplete label={`${t('commonFields.contact')}*`} name="id_contact"
                    options={contacts}
                    value={dto.id_contact}
                    onChange={handleInputChange}
                    error={Boolean(errorInputs.id_contact?.isError)}
                    helperText={errorInputs.id_contact?.messageError}
                />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <GenericAutocomplete label={`${t('commonFields.business')}*`} name="id_client"
                    options={bussines}
                    value={dto.id_client}
                    onChange={handleInputChange}
                    error={Boolean(errorInputs.id_client?.isError)}
                    helperText={errorInputs.id_client?.messageError}
                />
            </Grid>

            {/* Details */}
            <Grid size={{ xs: 12, md: 8 }}>
                <TextField fullWidth multiline rows={1} name="details" label={t('commonFields.details')} size="small" value={dto?.details || ''} onChange={handleInputChange} />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth required size="small"
                    error={Boolean(errorInputs.area?.isError)}
                >
                <InputLabel>Área o equipo comercial</InputLabel>
                <Select name='area' label='Area'
                    value={dto.area}
                    onChange={handleInputChange}
                >
                    {areas.map(cat => <MenuItem key={cat.code} value={cat.code}>{cat.name}</MenuItem>)}
                </Select>
                {errorInputs.area?.isError && (<FormHelperText>{errorInputs.area.messageError}</FormHelperText>)}
                </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth required size="small"
                    error={Boolean(errorInputs.business_line?.isError)}
                >
                    <InputLabel>Línea de negocio</InputLabel>
                    <Select name='business_line' label='business_line'
                        value={dto.business_line}
                        onChange={handleInputChange}
                        MenuProps={{PaperProps: {className: 'select-dropdown-scroll'}}}
                    >
                        {bussinesLine.map(cat => <MenuItem key={cat.code} value={cat.code}>{cat.name}</MenuItem>)}
                    </Select>
                    {errorInputs.business_line?.isError && ( <FormHelperText>{errorInputs.business_line.messageError}</FormHelperText> )}
                </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth required size="small"
                    error={Boolean(errorInputs.opportunity_stage?.isError)}
                >
                    <InputLabel>Etapa</InputLabel>
                    <Select name='opportunity_stage' label={t('commonFields.stage')}
                        value={dto.opportunity_stage}
                        onChange={handleData}
                        MenuProps={{PaperProps: {className: 'select-dropdown-scroll'}}}
                    >
                        {oportunityStage.map(cat => <MenuItem key={cat.code} value={cat.code}>{cat.name}</MenuItem>)}
                    </Select>
                    {errorInputs.opportunity_stage?.isError && (<FormHelperText>{errorInputs.opportunity_stage.messageError}</FormHelperText>)}
                </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker name="expected_closing_date" label={t('crm.expectedClosingDate')} minDate={new Date()} inputFormat="dd/MM/yyyy"
                        value={dto.expected_closing_date}
                        onChange={(e) => handleChangeDate(e, 'expected_closing_date')}
                        renderInput={(params) => (<TextField size="small" fullWidth  InputLabelProps={{ shrink: true }} {...params} />)}
                    />
                </LocalizationProvider>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker name="proposed_date" label={t('crm.proposalDelivery')} minDate={new Date()} inputFormat="dd/MM/yyyy"
                        value={dto.expected_closing_date}
                        onChange={(e) => handleChangeDate(e, 'proposed_date')}
                        renderInput={(params) => (<TextField size="small" fullWidth  InputLabelProps={{ shrink: true }} {...params} />)}
                    />
                </LocalizationProvider>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <TextField fullWidth size="small" name="closing_probability" label={t('crm.probabilityOfClosure')} type="number" className='input-no-spinner' value={dto.closing_probability} onChange={handleData} />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <TextField fullWidth size="small" name="identified_competence" label={t('crm.identifiedCompetence')} value={dto.identified_competence || ''} onChange={handleInputChange} />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <TextField fullWidth size="small" name="cost_center" label={t('crm.costCenter')} value={dto.cost_center || ''} onChange={handleInputChange} />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth required size="small"
                    error={Boolean(errorInputs.paymentTermDays?.isError)}
                >
                    <InputLabel>{t('crm.paymentMethods')}</InputLabel>
                    <Select name='paymentTermDays' label='paymentTermDays'
                        value={dto.paymentTermDays}
                        onChange={handleInputChange}
                        MenuProps={{PaperProps: {className: 'select-dropdown-scroll'}}}
                    >
                        {daysPaymets.map(i => <MenuItem key={i} value={i}>{i}</MenuItem>)}
                    </Select>
                    {errorInputs.paymentTermDays?.isError && ( <FormHelperText>{errorInputs.paymentTermDays.messageError}</FormHelperText> )}
                </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <TextField fullWidth required name="contract_time" label="Tiempo de contrato (Meses)" size="small" type="number" className='input-no-spinner'
                    value={dto?.contract_time || ''}
                    error={Boolean(errorInputs.contract_time?.isError)}
                    helperText={errorInputs.contract_time?.messageError || ''}
                    onChange={handleData}
                />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <TextField fullWidth name="approved_budget" label={t('crm.approvedBudget')} size="small" type="number" className='input-no-spinner' value={dto?.approved_budget || ''} onChange={handleInputChange} />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <TextField fullWidth name="client_need_rate" label={t('crm.customerNeed')} size="small" value={dto?.client_need_rate || ''} onChange={handleInputChange} />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <TextField fullWidth name="identified_pain" label={t('crm.identifiedPain')} size="small" value={dto?.identified_pain || ''} onChange={handleInputChange} />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <TextField fullWidth name="step_to_follow" label={t('crm.stepsToFollow')} size="small" value={dto?.step_to_follow || ''} onChange={handleInputChange} />
            </Grid>

            {/* Observations */}
            <Grid size={{ xs: 12, md:12 }} >
                <TextField fullWidth multiline rows={2} name="observations" label={t('crm.observations')} size="small" value={dto?.observations || ''} onChange={handleInputChange}
                    InputProps={{ sx: { overflowY: 'auto', maxHeight: '100px' }}}
                />
            </Grid>
            {!["Cierre No Ganado", "Cierre Ganado", "Acuerdo"].includes(dto.opportunity_stage) && (
                <Grid size={{ xs: 12, md: 4 }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker name="closing_date" label="Fecha de cierre"
                            value={dto.closing_date}
                            onChange={(e) => handleChangeDate(e, 'closing_date')}
                            renderInput={(params) => <TextField size="small" fullWidth InputLabelProps={{ shrink: true }} {...params} />}
                            inputFormat="dd/MM/yyyy"
                        />
                    </LocalizationProvider>
                </Grid>
            )}

            <Grid size={{ xs: 12, md: 4 }}>
                <TextField fullWidth  name="closing_conditions" label={t('crm.specialClosingConditions')} size="small" value={dto?.closing_conditions || ''} onChange={handleInputChange} />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <TextField fullWidth  name="client_order_number" label={t('crm.customerOrderNumber')} size="small" value={dto?.client_order_number || ''} onChange={handleInputChange} />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <TextField fullWidth name="delivery_address" label={t('commonFields.deliveryAddress')} size="small" value={dto.delivery_address || ''} onChange={handleInputChange} />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
                <TextField fullWidth name="phone" label={t('commonFields.phone')} size="small" type='number' className='input-no-spinner' value={dto.phone || ''} onChange={handleInputChange} />
            </Grid>

            <Grid size={{ xs: 12, md: 1 }}>
                <TextField fullWidth name="extension" label={t('commonFields.extension')} size="small" type='number' className='input-no-spinner' value={dto.extension || ''} onChange={handleData} />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <TextField fullWidth disabled name="created_by" label={t('commonFields.seller')} size="small" value={dto.user_name !== '' ? dto.user_name : dto.created_by} />
            </Grid>

            {["Cierre No Ganado", "Cierre Ganado"].includes(dto.opportunity_stage) && (
                <>
                    <Grid size={{ xs: 12, md: 4 }}>        
                        <MultipleSelect
                            data={reasons.filter(r => r.type === 'won' && dto.opportunity_stage === 'Cierre Ganado' || r.type === 'lost' && dto.opportunity_stage === 'Cierre No Ganado')}
                            values={typeof dto.reasons_id === 'string' && dto.reasons_id.length > 0 ? dto.reasons_id.split(',') : []}
                            handleChange={handleMultipleSelect}
                            label={'Motivos'}
                        />
                    </Grid>
                    {((Array.isArray(dto.reasons_id) ? dto.reasons_id : String(dto.reasons_id || '').split(','))
                        .map(String)
                        .map(s => s.trim())
                        .some(r => ['7','14'].includes(r))) && (
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField fullWidth required name="reason_other" label="Otro Motivo" size="small" value={dto.reason_other} onChange={handleData} />
                        </Grid>
                    )}
                </>
            )}
        </Grid>
    </>
  )
});